/**
 * Contractor Company Portal - Jobs Page
 * 
 * List and manage all company jobs with filters.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Loader2,
  Building2,
  Search,
  RefreshCw,
  LogOut,
  ChevronRight,
  MapPin,
  Clock,
  AlertTriangle,
  CheckCircle,
  FileText,
  User,
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const api = axios.create({ baseURL: `${API_URL}/api` });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('contractor_portal_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ============== COMPONENTS ==============

const StatusBadge = ({ status }) => {
  const statusStyles = {
    'ASSIGNED': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    'Assigned': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    'AWAITING_QUOTE': 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    'Awaiting Quote': 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    'Quote Awaiting Approval': 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    'APPROVED': 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    'Approved': 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    'SCHEDULED': 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
    'Scheduled': 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
    'IN_PROGRESS': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    'In Progress': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    'COMPLETED': 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    'Completed': 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    'Approved for Invoicing': 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    'Invoice Submitted': 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  };

  return (
    <Badge className={`${statusStyles[status] || 'bg-slate-500/20 text-slate-400 border-slate-500/30'} border text-xs`}>
      {status}
    </Badge>
  );
};

const SLABadge = ({ status }) => {
  if (status === 'OVERDUE') return <Badge className="bg-red-500 text-white text-xs">Overdue</Badge>;
  if (status === 'DUE_SOON') return <Badge className="bg-amber-500 text-white text-xs">Due Soon</Badge>;
  if (status === 'ON_TRACK') return <Badge className="bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 text-xs">On Track</Badge>;
  return <Badge className="bg-slate-500/20 text-slate-400 text-xs">N/A</Badge>;
};

const JobCard = ({ job, onClick }) => (
  <Card 
    className="border-slate-700 bg-slate-800 hover:border-slate-600 transition-colors cursor-pointer"
    onClick={onClick}
  >
    <CardContent className="p-4">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="font-semibold text-white">{job.job_reference || job.id}</h3>
          <p className="text-sm text-slate-400 line-clamp-1">{job.title || job.description}</p>
        </div>
        <ChevronRight className="w-5 h-5 text-slate-500" />
      </div>
      
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <StatusBadge status={job.status} />
        <SLABadge status={job.sla_status} />
      </div>
      
      <div className="space-y-1.5 text-xs text-slate-400">
        <div className="flex items-center gap-2">
          <MapPin className="w-3 h-3" />
          <span>{job.site_name || job.park_name || 'Unknown site'}</span>
        </div>
        {job.engineer_name && (
          <div className="flex items-center gap-2">
            <User className="w-3 h-3" />
            <span>{job.engineer_name}</span>
          </div>
        )}
        <div className="flex items-center gap-2 text-slate-500">
          <Clock className="w-3 h-3" />
          <span>{job.next_action}</span>
        </div>
      </div>
      
      {/* Quote/Invoice summary */}
      {(job.quote_summary || job.invoice_summary) && (
        <div className="mt-3 pt-3 border-t border-slate-700 flex gap-4 text-xs">
          {job.quote_summary && (
            <div className="flex items-center gap-1 text-slate-400">
              <FileText className="w-3 h-3" />
              <span>Quote: £{job.quote_summary.total_ex_vat?.toFixed(0) || 0}</span>
            </div>
          )}
          {job.invoice_summary && (
            <div className="flex items-center gap-1 text-slate-400">
              <span>Invoice: £{job.invoice_summary.invoice_amount?.toFixed(0) || 0}</span>
            </div>
          )}
        </div>
      )}
    </CardContent>
  </Card>
);

// ============== MAIN COMPONENT ==============

export default function ContractorCompanyJobs() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [status, setStatus] = useState(searchParams.get('status') || '');
  const [overdue, setOverdue] = useState(searchParams.get('overdue') === 'true');

  const contractorName = localStorage.getItem('contractor_portal_name') || 'Contractor Portal';

  const loadJobs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (status) params.set('status', status);
      if (overdue) params.set('overdue', 'true');
      params.set('limit', '100');
      
      const res = await api.get(`/contractor-company/jobs?${params.toString()}`);
      setJobs(res.data.data || []);
      setTotal(res.data.meta?.total || res.data.data?.length || 0);
    } catch (err) {
      console.error('Load error:', err);
      if (err.response?.status === 401) {
        navigate('/contractor-company/login');
      }
    } finally {
      setLoading(false);
    }
  }, [search, status, overdue, navigate]);

  useEffect(() => {
    const token = localStorage.getItem('contractor_portal_token');
    if (!token) {
      navigate('/contractor-company/login');
      return;
    }
    loadJobs();
  }, [loadJobs, navigate]);

  const handleLogout = () => {
    localStorage.removeItem('contractor_portal_token');
    localStorage.removeItem('contractor_portal_id');
    localStorage.removeItem('contractor_portal_name');
    navigate('/contractor-company/login');
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadJobs();
  };

  const handleJobClick = (job) => {
    // Navigate to job detail (could be a modal or new page)
    console.log('Job clicked:', job.id);
    // For now, just log - could implement a detail modal
  };

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-xl flex items-center justify-center">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">{contractorName}</h1>
              <p className="text-xs text-slate-400">Jobs ({total})</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={loadJobs} disabled={loading} className="text-slate-300 hover:text-white">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-slate-300 hover:text-white">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-slate-800/50 border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-1">
            <Link 
              to="/contractor-company/dashboard" 
              className="px-4 py-3 text-sm font-medium text-slate-400 hover:text-white border-b-2 border-transparent"
            >
              Dashboard
            </Link>
            <Link 
              to="/contractor-company/jobs" 
              className="px-4 py-3 text-sm font-medium text-blue-400 border-b-2 border-blue-400"
            >
              Jobs
            </Link>
            <Link 
              to="/contractor-company/performance" 
              className="px-4 py-3 text-sm font-medium text-slate-400 hover:text-white border-b-2 border-transparent"
            >
              Performance
            </Link>
          </div>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Filters */}
        <Card className="mb-6 border-slate-700 bg-slate-800">
          <CardContent className="p-4">
            <form onSubmit={handleSearch} className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search jobs..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-10 bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
                    data-testid="job-search-input"
                  />
                </div>
              </div>
              
              <Select value={status || "all"} onValueChange={(val) => setStatus(val === "all" ? "" : val)}>
                <SelectTrigger className="w-48 bg-slate-700 border-slate-600 text-white">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent className="bg-slate-700 border-slate-600">
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="ASSIGNED">Assigned</SelectItem>
                  <SelectItem value="AWAITING_QUOTE">Awaiting Quote</SelectItem>
                  <SelectItem value="APPROVED">Approved</SelectItem>
                  <SelectItem value="SCHEDULED">Scheduled</SelectItem>
                  <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
                  <SelectItem value="COMPLETED">Completed</SelectItem>
                </SelectContent>
              </Select>
              
              <Button
                type="button"
                variant={overdue ? 'default' : 'outline'}
                className={overdue ? 'bg-red-500 hover:bg-red-600' : 'border-slate-600 text-slate-300'}
                onClick={() => setOverdue(!overdue)}
              >
                <AlertTriangle className="w-4 h-4 mr-2" />
                Overdue Only
              </Button>
              
              <Button type="submit" className="bg-blue-500 hover:bg-blue-600">
                <Search className="w-4 h-4 mr-2" />
                Search
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Jobs Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
          </div>
        ) : jobs.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <FileText className="w-8 h-8 text-slate-500" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">No Jobs Found</h3>
            <p className="text-slate-400">No jobs match your current filters.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {jobs.map((job) => (
              <JobCard key={job.id || job._id} job={job} onClick={() => handleJobClick(job)} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
